import styled from "@emotion/styled";
import { Button, Colors, Container, Label, Tooltip, Fonts } from "@snapchat/snap-design-system";
import { ExternalLinkIcon } from "@snapchat/snap-design-system-icons";
import { type FC } from "react";
import { InfoTooltip } from "../../../common/components";
import { AppStatusCell } from "../../components/AppStatusCell";
import { AppVersionStatus } from "../../models";
import { useGetAppInfo } from "../hooks/useGetAppInfo";

const tooltipText = {
    prodStatus: "The Camera Kit status of the active version selected for the production environment.",
    prodVersion: "Click to select a version for production",
    stageStatus: "The Camera Kit status of the active version selected for the staging environment.",
    stageVersion: "Click to select a version for staging",
};

export const AppOverview: FC = () => {
    const {
        appInfo: {
            appUrl,
            prodLiveVersion,
            prodStatus,
            prodVersionUrl,
            stagingVersionUrl,
            stageStatus,
            stagingLiveVersion,
        },
        organizationName,
    } = useGetAppInfo();

    return (
        <Wrapper>
            <CopyContainer>
                <GrayLabel>Organization</GrayLabel>
                <Copy title={organizationName ?? ""} data-testid="app-organization">
                    {organizationName}
                </Copy>
            </CopyContainer>
            <CopyContainer>
                <GrayLabel>
                    Production Camera Kit Status
                    <InfoTooltip content={tooltipText.prodStatus} />
                </GrayLabel>
                <VersionStatusWrapper>
                    <AppStatusCell data-testid="app-status" status={prodStatus ?? ""} />
                    {prodStatus === AppVersionStatus.UNKNOWN ? (
                        <LinkOutButton type="link" href={appUrl ?? ""} target="_blank">
                            <Tooltip content={tooltipText.prodVersion}>
                                <span>{prodLiveVersion}</span>
                            </Tooltip>
                            <ExternalLinkIcon />
                        </LinkOutButton>
                    ) : (
                        <LinkOutButton type="link" href={prodVersionUrl ?? ""} target="_blank">
                            {prodLiveVersion}
                            <ExternalLinkIcon />
                        </LinkOutButton>
                    )}
                </VersionStatusWrapper>
            </CopyContainer>
            <CopyContainer>
                <GrayLabel>
                    Staging Camera Kit Status
                    <InfoTooltip content={tooltipText.stageStatus} />
                </GrayLabel>
                <VersionStatusWrapper>
                    <AppStatusCell data-testid="app-status" status={stageStatus ?? ""} />
                    {stageStatus === AppVersionStatus.UNKNOWN ? (
                        <LinkOutButton type="link" href={appUrl ?? ""} target="_blank">
                            <Tooltip content={tooltipText.stageVersion}>
                                <span>{stagingLiveVersion}</span>
                            </Tooltip>
                            <ExternalLinkIcon />
                        </LinkOutButton>
                    ) : (
                        <LinkOutButton type="link" href={stagingVersionUrl ?? ""} target="_blank">
                            {stagingLiveVersion}
                            <ExternalLinkIcon />
                        </LinkOutButton>
                    )}
                </VersionStatusWrapper>
            </CopyContainer>
        </Wrapper>
    );
};

const Wrapper = styled(Container)`
    display: flex;
    justify-content: space-between;
    padding: 16px 24px;
`;

const GrayLabel = styled(Label)`
    ${Fonts.Paragraph.P300};
    display: flex;
    gap: 5px;
    align-items: center;
    color: ${Colors.Black.V50};
`;

const CopyContainer = styled.div`
    overflow: hidden;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 8px;

    max-width: 250px;
`;

const Copy = styled.p`
    ${Fonts.Paragraph.P200};
    overflow: hidden;

    width: 100%;
    margin-top: 6px;

    text-overflow: ellipsis;
    white-space: nowrap;
`;

const LinkOutButton = styled(Button)`
    display: flex;

    &.sds-base-button.ant-btn {
        justify-content: flex-start;

        min-height: initial;
        padding: 0;
        padding-top: 0 !important;

        font-weight: 400;
    }
`;

const VersionStatusWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    justify-content: flex-start;

    p {
        margin: 0;
    }
`;
